import {
  findRefreshErrorMessage,
  isE2eIdToken,
  isIdTokenFresh,
  isUnrecoverableRefreshError,
  isUnrecoverableSdkTokenErrorCode,
} from "@repo/common"
import { FirebaseError } from "firebase/app"
import { TokenRefreshUnavailableError } from "./errors"

/**
 * A long-lived tab stuck in a token failure (broken storage, a network that
 * blocks securetoken) still reports now and then, without one event per token
 * acquisition.
 */
const TOKEN_FAILURE_REPORT_INTERVAL_MS = 24 * 60 * 60 * 1000

export interface Tokens {
  idToken: string
  refreshToken: string
}

interface CachedTokens {
  idToken: string
  // storeAuthTokens writes the ID token first, so a refused second write
  // leaves storage holding an ID token only.
  refreshToken: string | null
}

/** The slice of the Firebase `User` this module needs. */
interface SdkUser {
  refreshToken: string
  getIdToken: (forceRefresh?: boolean) => Promise<string>
}

/** The slice of `Storage` this module needs. */
interface TokenStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

interface IdTokenSourceDeps {
  storage: TokenStorage
  /** Resolves once the SDK has rehydrated its session on page load. */
  authReady: Promise<void>
  getCurrentUser: () => SdkUser | null
  /** Mints new tokens over Google's securetoken REST endpoint. */
  refreshOverRest: (refreshToken: string) => Promise<Tokens>
  reportError: (error: Error) => void
  /**
   * Called with the cause whenever a session that did exist turns out to be
   * gone, so a forced logout can be traced back to what ended it.
   */
  reportSessionEnd: (reason: string) => void
}

export interface IdTokenSource {
  /**
   * Returns a usable token, refreshing it if needed, or null if the session is
   * gone. Throws on transient failures, which must not end the session.
   */
  findIdToken: (forceRefresh?: boolean) => Promise<string | null>
  storeAuthTokens: (tokens: Tokens) => void
  clearAuthTokens: () => void
  /** Another tab removed the stored tokens, i.e. logged out. */
  handleStoredTokensRemoved: () => void
}

/**
 * Token cache and acquisition, kept free of the Firebase app so it can be
 * tested. auth.ts wires it to the real SDK, localStorage and securetoken.
 */
export const createIdTokenSource = ({
  storage,
  authReady,
  getCurrentUser,
  refreshOverRest,
  reportError,
  reportSessionEnd,
}: IdTokenSourceDeps): IdTokenSource => {
  /**
   * In-memory mirror of the latest tokens. When the browser refuses storage
   * writes, storage never advances, and without this every request would pay
   * a fresh token round-trip to Google. Only a logout — here, or in another
   * tab via handleStoredTokensRemoved — clears it: an empty storage alone
   * can't tell a logout from a write that never landed.
   */
  let memoryTokens: Tokens | null = null

  let tokenRefreshPromise: Promise<string | null> | null = null
  let tokenRefreshIsForced = false
  let sdkTokenFailureReportedAt: number | null = null
  let refreshFailureReportedAt: number | null = null

  /**
   * Best-effort cache write. A browser that refuses storage writes (full disk,
   * exhausted quota) must not cost us a token we already hold — callers return
   * the token either way.
   */
  const storeAuthTokens = ({ idToken, refreshToken }: Tokens) => {
    memoryTokens = { idToken, refreshToken }

    try {
      storage.setItem("idToken", idToken)
      storage.setItem("refreshToken", refreshToken)
    } catch (error) {
      console.error("Failed to store auth tokens:", error)
    }
  }

  /**
   * Best-effort, like the write: a browser that refuses storage access throws
   * on removes too, and a throw here would strand logout before it navigates.
   */
  const clearAuthTokens = () => {
    memoryTokens = null

    try {
      storage.removeItem("idToken")
      storage.removeItem("refreshToken")
    } catch (error) {
      console.error("Failed to clear auth tokens:", error)
    }
  }

  const handleStoredTokensRemoved = () => {
    memoryTokens = null
  }

  /** Storage that can't be read counts as empty. */
  const findStoredTokens = (): CachedTokens | null => {
    try {
      const idToken = storage.getItem("idToken")
      if (!idToken) return null

      return { idToken, refreshToken: storage.getItem("refreshToken") }
    } catch (error) {
      console.error("Failed to read auth tokens:", error)
      return null
    }
  }

  /**
   * Newest first: memory holds the latest token even when storage writes
   * fail, storage covers cold loads, the seeded e2e token, and tokens another
   * tab refreshed.
   */
  const findCachedTokens = (): CachedTokens[] => {
    const storedTokens = findStoredTokens()
    return [memoryTokens, storedTokens].filter((tokens) => tokens !== null)
  }

  const isUsableIdToken = (idToken: string): boolean =>
    isE2eIdToken(idToken) || isIdTokenFresh(idToken)

  const findIdToken = async (forceRefresh = false): Promise<string | null> => {
    if (!forceRefresh) {
      const cachedIdToken = findCachedTokens().find(({ idToken }) =>
        isUsableIdToken(idToken),
      )?.idToken
      if (cachedIdToken) return cachedIdToken
    }

    // A non-forced caller may join any in-flight refresh. A forced caller may
    // only join another forced one — otherwise it would be handed back the
    // very token it is trying to replace.
    if (tokenRefreshPromise && (tokenRefreshIsForced || !forceRefresh)) {
      return tokenRefreshPromise
    }

    // Assign synchronously: every await lives inside acquireIdToken, so no
    // other caller can slip between the check above and this assignment.
    tokenRefreshIsForced = forceRefresh
    const pending = acquireIdToken(forceRefresh).finally(() => {
      if (tokenRefreshPromise === pending) tokenRefreshPromise = null
    })
    tokenRefreshPromise = pending

    return pending
  }

  const shouldReportSdkTokenFailure = (): boolean =>
    sdkTokenFailureReportedAt === null ||
    Date.now() - sdkTokenFailureReportedAt >= TOKEN_FAILURE_REPORT_INTERVAL_MS

  const shouldReportRefreshFailure = (): boolean =>
    refreshFailureReportedAt === null ||
    Date.now() - refreshFailureReportedAt >= TOKEN_FAILURE_REPORT_INTERVAL_MS

  const acquireIdToken = async (
    forceRefresh: boolean,
  ): Promise<string | null> => {
    await authReady

    const currentUser = getCurrentUser()

    if (currentUser) {
      try {
        const idToken = await currentUser.getIdToken(forceRefresh)
        storeAuthTokens({ idToken, refreshToken: currentUser.refreshToken })

        // Only the SDK itself succeeding ends the incident: the REST fallback
        // succeeds throughout one, e.g. while browser storage stays broken.
        sdkTokenFailureReportedAt = null

        return idToken
      } catch (error) {
        console.error("Failed to get ID token from Firebase SDK:", error)

        // A dead session is the ordinary end of a login, not an exception.
        if (
          error instanceof FirebaseError &&
          isUnrecoverableSdkTokenErrorCode(error.code)
        ) {
          reportSessionEnd(`sdk:${error.code}`)
          return null
        }

        // Every query and retry lands here during an incident, so report once
        // per incident, and again daily while it lasts, rather than flooding
        // Sentry.
        if (shouldReportSdkTokenFailure()) {
          sdkTokenFailureReportedAt = Date.now()
          reportError(error instanceof Error ? error : new Error(String(error)))
        }

        // The SDK also fails when it can't persist the refreshed user (broken
        // browser storage), so mint the token over REST instead of giving up.
        return refreshWithRefreshToken(currentUser.refreshToken)
      }
    }

    const cachedTokens = findCachedTokens()

    // An e2e token has no real session behind it; its seeded refresh token
    // would only get INVALID_REFRESH_TOKEN back and clear the session.
    if (cachedTokens.some(({ idToken }) => isE2eIdToken(idToken))) return null

    const refreshToken =
      cachedTokens.find(({ refreshToken }) => refreshToken)?.refreshToken ??
      null

    // No cached tokens at all is simply a logged-out browser, not a loss.
    if (!refreshToken && cachedTokens.length > 0) {
      reportSessionEnd("no-refresh-token")
    }

    return refreshWithRefreshToken(refreshToken)
  }

  /**
   * Returns null only when Google says the refresh token is dead. Transient
   * failures throw, so callers surface an error instead of logging out.
   */
  const refreshWithRefreshToken = async (
    refreshToken: string | null,
  ): Promise<string | null> => {
    if (!refreshToken) return null

    try {
      const tokens = await refreshOverRest(refreshToken)
      storeAuthTokens(tokens)
      refreshFailureReportedAt = null
      return tokens.idToken
    } catch (error) {
      // Never log or rethrow the raw axios error: its request config holds the
      // refresh token, and Sentry serialises it from both.
      const refreshError = new TokenRefreshUnavailableError(
        `Token refresh failed: ${findRefreshErrorMessage(error) ?? "no response"}`,
      )
      console.error(refreshError)

      // Only end the session when Google says the refresh token itself is
      // dead. Ending it on any error means a single offline moment or 5xx
      // logs the store out with no way back but a re-login.
      if (!isUnrecoverableRefreshError(error)) {
        // Callers drop this error rather than report it once per query, so
        // report it here, once per incident and again daily while it lasts:
        // a network that blocks securetoken for good must not go unseen.
        if (shouldReportRefreshFailure()) {
          refreshFailureReportedAt = Date.now()
          reportError(refreshError)
        }
        throw refreshError
      }

      reportSessionEnd(`rest:${findRefreshErrorMessage(error)}`)
      clearAuthTokens()
      return null
    }
  }

  return {
    findIdToken,
    storeAuthTokens,
    clearAuthTokens,
    handleStoredTokensRemoved,
  }
}
